import React, { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { SortOrder } from 'types/general.types';
import { ReactComponent as ChevronIcon } from 'resources/images/chevron-down.svg';
import { Flex, FlexProps, Icon } from '@chakra-ui/react';

export interface ListSortColumnProps<SortBy> extends FlexProps {
  sortParams: {
    sortBy: SortBy;
    sortOrder: SortOrder;
  };
  children?: ReactNode;
  column: SortBy;
  initSortOrder: SortOrder;
  disabled?: boolean;
}

export const ListSortColumn = <SortBy,>({
  sortParams,
  column,
  initSortOrder,
  children,
  disabled,
  ...rest
}: ListSortColumnProps<SortBy>) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const inverse = (current: SortOrder): SortOrder =>
    current === 'asc' ? 'desc' : 'asc';

  const isActive = sortParams.sortBy === column;
  const direction = isActive ? sortParams.sortOrder : initSortOrder;

  const updateSortParams = (sortBy: SortBy, sortOrder: SortOrder) => {
    const currentQueryParams = queryString.parse(search);
    const newQueryParams = {
      ...currentQueryParams,
      sortBy,
      sortOrder,
    };
    navigate({
      search: queryString.stringify(newQueryParams),
    });
  };

  return (
    <Flex
      justifyContent="start"
      alignItems="center"
      role={disabled ? '' : 'button'}
      data-testid={`direction-${direction}`}
      onClick={() => {
        if (disabled) {
          return;
        }
        const sortBy = column;
        const sortOrder = isActive
          ? inverse(sortParams.sortOrder)
          : initSortOrder;
        updateSortParams(sortBy, sortOrder);
      }}
      _hover={{
        svg: {
          opacity: 1,
        },
      }}
      sx={{ color: 'M.60', fontSize: 'xs', fontWeight: 500, lineHeight: 1.4 }}
      {...rest}
    >
      {children}
      <Icon
        fill="M.60"
        transition="opacity 200ms ease-in-out"
        transform={direction === 'asc' ? 'rotate(180deg)' : ''}
        opacity={0}
        width="12px"
        height="12px"
        mx={1}
        as={ChevronIcon}
        {...(isActive && { opacity: 1, fill: 'B.80' })}
      />
    </Flex>
  );
};
